<template>
  <div class="price-table">
    <accordian-display :icon="icon"
      :title="title"
      :container-class="containerClass"
      :default-open="true">
      <template slot="header">
        <tr>
          <th width="50%">
            Start: {{ priceTable.opts.startDate | short_date_time_format(priceTable.opts.Lot.timezone) }}<br>
            End: {{ priceTable.opts.endDate | short_date_time_format(priceTable.opts.Lot.timezone) }}
          </th>
          <th colspan="3">{{ bookingPeriod }}</th>
        </tr>
        <tr>
          <th class="bg-primary text-white text-right"><strong>Description</strong></th>
          <th class="bg-primary text-white"><strong>Units</strong></th>
          <th class="bg-primary text-white"><strong>Cost</strong></th>
          <th class="bg-primary text-white"><strong>Total</strong></th>
        </tr>
      </template>
      <tr v-if="priceTable.priceBreakdown.days">
        <td width="50%" class="font-weight-bold">Daily Rate</td>
        <td width="15%">{{ priceTable.priceBreakdown.days }} days</td>
        <td width="20%">
          {{ priceTable.opts.Rate.dailyRate | to_dollar_amount }}/day
        </td>
        <td width="15%">
          {{ priceTable.priceBreakdown.dailySubTotal | to_dollar_amount }}
        </td>
      </tr>
      <tr v-if="priceTable.priceBreakdown.hours">
        <td class="font-weight-bold">Hourly Rate</td>
        <td>{{ priceTable.priceBreakdown.hours }} hours</td>
        <td>
          {{ priceTable.opts.Rate.hourlyRate | to_dollar_amount }}/hr
        </td>
        <td>
          {{ priceTable.priceBreakdown.hourlySubTotal | to_dollar_amount }}
        </td>
      </tr>
      <tr v-if="priceTable.priceBreakdown.overnightSubTotal > 0">
        <td class="font-weight-bold">Overnight Rate</td>
        <td>{{ priceTable.priceBreakdown.overnightHours }} hours</td>
        <td>
          {{ priceTable.opts.Rate.overnightRate | to_dollar_amount }}
        </td>
        <td>
          {{ priceTable.priceBreakdown.overnightSubTotal | to_dollar_amount }}
        </td>
      </tr>
      <tr v-if="priceTable.priceBreakdown.weekendSubTotal > 0">
        <td class="font-weight-bold">Weekend Rate</td>
        <td>{{ priceTable.priceBreakdown.weekendDays }} weekends</td>
        <td>
          {{ priceTable.opts.Rate.weekendRate | to_dollar_amount }}
        </td>
        <td>
          {{ priceTable.priceBreakdown.weekendSubTotal | to_dollar_amount }}
        </td>
      </tr>
      <template v-for="(extra, i) in priceTable.opts.extras">
        <template v-if="extra.rateType === 'fixed'">
          <tr :key="i">
            <td class="font-weight-bold">{{ extra.name }}</td>
            <td>1</td>
            <td>{{ extra.cost | to_dollar_amount }}</td>
            <td>{{ extra.cost | to_dollar_amount }}</td>
          </tr>
        </template>
        <template v-if="extra.rateType === 'rate'">
          <tr :key="i" v-if="priceTable.priceBreakdown.days">
            <td class="font-weight-bold">{{ extra.name }} (Daily Rate)</td>
            <td>{{ priceTable.priceBreakdown.days }} days</td>
            <td>{{ extra.dailyRate | to_dollar_amount }}/day</td>
            <td>
              {{ extra.dailyRate * priceTable.priceBreakdown.days | to_dollar_amount }}
            </td>
          </tr>
          <tr :key="i" v-if="priceTable.priceBreakdown.hours">
            <td class="font-weight-bold">{{ extra.name }} (Hourly Rate)</td>
            <td>
              {{ priceTable.priceBreakdown.hours }} hours
            </td>
            <td>{{ extra.hourlyRate | to_dollar_amount }}/hr</td>
            <td>
              {{ extra.hourlyRate * priceTable.priceBreakdown.hours
                 | to_dollar_amount }}
            </td>
          </tr>
          <tr v-if="priceTable.priceBreakdown.overnightSubTotal > 0" :key="i">
            <td class="font-weight-bold">{{ extra.name }} (Overnight Rate)</td>
            <td>{{ priceTable.priceBreakdown.overnightHours }} hours</td>
            <td>{{ extra.overnightRate | to_dollar_amount }}</td>
            <td>
              {{ extra.overnightRate * priceTable.priceBreakdown.overnightHours |
                to_dollar_amount }}
            </td>
          </tr>
          <tr v-if="priceTable.priceBreakdown.weekendSubTotal > 0" :key="i">
            <td class="font-weight-bold">{{ extra.name }} (Weekend Rate)</td>
            <td>{{ priceTable.priceBreakdown.weekendDays }} weekends</td>
            <td>{{ extra.weekendRate | to_dollar_amount }}</td>
            <td>
              {{ extra.weekendRate * priceTable.priceBreakdown.weekendDays | to_dollar_amount }}
            </td>
          </tr>
        </template>
      </template>
      <tr>
        <td colspan="3" class="font-weight-bold">{{ showDetails || priceTable.opts.percentDiscount > 0 ? 'Sub Total' : 'Total' }}</td>
        <td>
          <strong>{{ priceTable.priceBreakdown.preDiscountCost || priceTable.priceBreakdown.subTotalCost | to_dollar_amount }}</strong>
        </td>
      </tr>
      <tr v-if="priceTable.opts.percentDiscount > 0">
        <td colspan="3" class="font-weight-bold">Discount</td>
        <td>
          <strong class="credit">
            <em>{{ priceTable.opts.percentDiscount }}% off*</em>
          </strong>
        </td>
      </tr>
      <tr v-if="showDetails && priceTable.priceBreakdown.extraCredit > 0">
        <td colspan="3" class="font-weight-bold">Credit</td>
        <td>
          <strong class="credit">
            {{ priceTable.priceBreakdown.extraCredit | to_dollar_amount }}
          </strong>
        </td>
      </tr>
      <tr v-if="showDetails && priceTable.priceBreakdown.alreadyPaid > 0">
        <td colspan="3" class="font-weight-bold">Already Paid</td>
        <td>
          <strong class="already-paid">
            {{ priceTable.priceBreakdown.alreadyPaid | to_dollar_amount }}
          </strong>
        </td>
      </tr>
      <tr v-if="showDetails && useCredits || priceTable.priceBreakdown.originalCredit < 0">
        <td colspan="3" class="font-weight-bold">Account Balance</td>
        <td>
          <strong v-bind:class="{
            'red': priceTable.priceBreakdown.originalCredit < 0, 'green': priceTable.priceBreakdown.originalCredit > 0 }">
            {{ priceTable.priceBreakdown.originalCredit | to_dollar_amount_neg }}</strong>
        </td>
      </tr>
      <tr v-if="showDetails && balancePayment">
        <td colspan="3" class="font-weight-bold">Account Balance Payment</td>
        <td>
          <strong>{{ balancePayment | to_dollar_amount }}</strong>
        </td>
      </tr>
      <tr v-if="showDetails || priceTable.opts.percentDiscount > 0">
        <td colspan="3" class="font-weight-bold">Total</td>
        <td>
          <strong>{{ priceTable.priceBreakdown.totalCost | to_dollar_amount }}</strong>
        </td>
      </tr>
      <tr v-if="priceTable.opts.percentDiscount > 0">
        <td colspan="3" class="font-weight-bold">Savings</td>
        <td>
          <strong class="credit">
            <em>{{ priceTable.priceBreakdown.preDiscountCost - priceTable.priceBreakdown.subTotalCost | to_dollar_amount }}</em>
          </strong>
        </td>
      </tr>
      <tr v-if="showDetails && useCredits">
        <td colspan="3" class="font-weight-bold">Remaining Credits</td>
        <td>
          <strong v-bind:class="{
            'red': priceTable.priceBreakdown.remainingCredit < 0, 'green': priceTable.priceBreakdown.remainingCredit > 0 }">
            {{ priceTable.priceBreakdown.remainingCredit | to_dollar_amount_neg }}</strong>
        </td>
      </tr>
    </accordian-display>
    <p v-if="priceTable.opts.percentDiscount > 0" class="alert alert-success">
      * Discount does not apply on any optional extras, parking levies, tolls,
      kilometres travelled or already discounted rates (overnight and weekend
      rates).
    </p>
  </div>
</template>

<script>
import AccordianDisplay from '@/components/displays/AccordianDisplay'
export default {
  name: 'PriceTable',
  data () {
    return {}
  },
  components: {
    AccordianDisplay
  },
  props: {
    icon: {
      type: String,
      default () {
        return 'fa-dollar-sign'
      }
    },
    containerClass: {
      type: String,
      default () {
        return 'secondary'
      }
    },
    title: {
      type: String,
      default () {
        return 'Price Breakdown'
      }
    },
    priceTable: {
      type: Object,
      default () {
        return {
          reservation: {},
          priceBreakdown: {},
          rate: {}
        }
      }
    },
    showDetails: {
      type: Boolean,
      default () {
        return false
      }
    },
    useCredits: {
      type: Boolean,
      default () {
        return false
      }
    }
  },
  computed: {
    balancePayment () {
      if (this.priceTable && this.priceTable.priceBreakdown.originalCredit < 0) {
        return Math.abs(this.priceTable.priceBreakdown.originalCredit)
      }
      return 0
    },
    bookingPeriod () {
      let duration = false
      let string = ''
      if (this.priceTable.opts.startDate && this.priceTable.opts.endDate) {
        let minutes = moment(this.priceTable.opts.endDate).diff(this.priceTable.opts.startDate, 'minutes')
        duration = moment.duration(minutes, 'minutes')
      }
      if (duration) {
        if (duration.get('days')) string += duration.get('days') + ' days '
        if (duration.get('hours')) string += duration.get('hours') + ' hours '
        if (duration.get('minutes')) string += duration.get('minutes') + ' minutes '
      }
      return string
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/displays/PriceTable.vue